// 格式化 duration（秒）为可读格式，如 "1d 2h 30m" 或 "90m"
export function formatDuration(seconds: number): string {
  if (!seconds || seconds <= 0) {
    return '-'
  }

  // 转换为整数，避免显示小数
  const totalSeconds = Math.floor(seconds)

  const days = Math.floor(totalSeconds / 86400)
  const hours = Math.floor((totalSeconds % 86400) / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const secs = totalSeconds % 60

  const parts: string[] = []
  if (days > 0) {
    parts.push(`${days}d`)
  }
  if (hours > 0) {
    parts.push(`${hours}h`)
  }
  if (minutes > 0) {
    parts.push(`${minutes}m`)
  }
  if (secs > 0) {
    parts.push(`${secs}s`)
  }

  return parts.length > 0 ? parts.join(' ') : '0s'
}

// 解析时间差字符串为秒数，支持 "1d13h18m16s"、"1d 13h 18m 16s"、"1.5h" 等格式
// 要求：d/h/m/s 必须按照顺序出现，每个单位只能出现一次，支持小数
export function parseDuration(durationStr: string): number | null {
  if (!durationStr || durationStr.trim() === '') {
    return null
  }

  // 移除所有空格，统一处理
  const cleaned = durationStr.trim().replace(/\s+/g, '')

  // 匹配模式：支持小数的数字+单位（d/h/m/s）
  // 使用全局匹配找到所有匹配项
  const pattern = /(\d+\.?\d*)([dhms])/gi
  const matches: Array<{ value: number; unit: string; index: number }> = []
  let match

  // 重置 pattern 的 lastIndex
  pattern.lastIndex = 0
  while ((match = pattern.exec(cleaned)) !== null) {
    const value = parseFloat(match[1])
    const unit = match[2].toLowerCase()
    matches.push({ value, unit, index: match.index! })
  }

  // 如果没有匹配到任何内容，返回 null
  if (matches.length === 0) {
    return null
  }

  // 检查是否有其他字符（不在匹配中的字符）
  // 通过检查所有匹配项是否连续覆盖整个字符串
  const sortedMatches = matches.sort((a, b) => a.index - b.index)
  let expectedIndex = 0
  for (const m of sortedMatches) {
    const matchStr = String(m.value) + m.unit
    if (m.index !== expectedIndex) {
      return null // 匹配项不连续，中间有其他字符
    }
    expectedIndex += matchStr.length
  }
  if (expectedIndex !== cleaned.length) {
    return null // 有未匹配的字符
  }

  // 检查顺序和唯一性
  const expectedOrder = ['d', 'h', 'm', 's']
  const foundUnits: Set<string> = new Set()
  let lastUnitIndex = -1

  for (const { unit } of matches) {
    // 检查单位是否重复
    if (foundUnits.has(unit)) {
      return null
    }

    // 检查顺序：当前单位必须在最后一个单位的后面
    const currentUnitIndex = expectedOrder.indexOf(unit)
    if (currentUnitIndex === -1) {
      return null // 无效的单位
    }
    if (currentUnitIndex <= lastUnitIndex) {
      return null // 顺序不对
    }

    foundUnits.add(unit)
    lastUnitIndex = currentUnitIndex
  }

  // 计算总秒数
  let totalSeconds = 0
  for (const { value, unit } of matches) {
    switch (unit) {
      case 'd':
        totalSeconds += value * 86400 // 1 day = 86400 seconds
        break
      case 'h':
        totalSeconds += value * 3600 // 1 hour = 3600 seconds
        break
      case 'm':
        totalSeconds += value * 60 // 1 minute = 60 seconds
        break
      case 's':
        totalSeconds += value
        break
    }
  }

  return totalSeconds
}

// 格式化区间名称，将 "[<-50]" 显示为 "<-50%"
export function formatBucketTitle(key: string): string {
  // 移除方括号
  let formatted = key.replace(/[[\]]/g, '')
  // 在数字后添加百分号，但保持符号不变
  formatted = formatted.replace(/(\d+)/g, '$1%')
  return formatted
}
